import React, { useState, useEffect, useCallback } from 'react';
import { deliveryAPI } from '../api';
import { Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, X, Filter, Link2, CalendarDays, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import Toast from '../components/Toast';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function getStatusClass(status) {
  if (!status) return 'status-pending';
  const s = status.toUpperCase();
  if (s.includes('DELIVER')) return 'status-delivered';
  if (s.includes('RETURN') || s.includes('PULL')) return 'status-returned';
  if (s.includes('TRANSIT')) return 'status-transit';
  return 'status-pending';
}

function calculateDueDate(deliveryDate, paymentTerms) {
  if (!deliveryDate || !paymentTerms) return null;
  
  // Extract number from payment terms (e.g., "Net 30" -> 30, "COD" -> 0, "30" -> 30)
  const match = paymentTerms.toString().match(/(\d+)/);
  const days = match ? parseInt(match[1]) : 0;
  
  if (days === 0) return null;
  
  const date = new Date(deliveryDate);
  date.setDate(date.getDate() + days);
  return date;
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
}

const emptyForm = {
  date: '', plate_number: '', so_number: '', document_no: '', invoice_number: '',
  customer_code: '', customer_name: '', amount: '', picker: '', checker_name: '', driver: '',
  helper_1: '', helper_2: '', destination: '', date_delivered: '',
  due_date: '', payment_terms: '', delivery_status: 'PENDING', remarks: '', color: ''
};

export default function Deliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deliveredDateFrom, setDeliveredDateFrom] = useState('');
  const [deliveredDateTo, setDeliveredDateTo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState('');
  const [toast, setToast] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
  const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);
  const [invoiceSearchTimer, setInvoiceSearchTimer] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const invoiceInputRef = React.useRef(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await deliveryAPI.getAll({ page, limit: 15, search, status: statusFilter, dateFrom, dateTo, deliveredDateFrom, deliveredDateTo, sortBy, order: sortOrder });
      setDeliveries(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }, [search, statusFilter, dateFrom, dateTo, deliveredDateFrom, deliveredDateTo, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let result;
      if (editId) {
        result = await deliveryAPI.update(editId, { ...form, _originalInvoiceNumber: originalInvoiceNumber });
      } else {
        result = await deliveryAPI.create(form);
      }
      setShowModal(false);
      setForm(emptyForm);
      setOriginalInvoiceNumber('');
      setEditId(null);
      load(pagination.page);

      // Show success toast
      const action = editId ? 'updated' : 'created';
      const label = form.invoice_number || form.customer_name || 'Delivery';
      setToast({
        message: `Delivery ${action} successfully: ${label}`,
        type: 'success',
        duration: 4000,
      });

      // Show cascade notification
      const cascadeCount = result?._cascadeUpdated || result?.data?._cascadeUpdated || 0;
      if (cascadeCount > 0) {
        setTimeout(() => {
          const code = form.customer_code ? ` (${form.customer_code})` : '';
          setToast({
            message: `Also updated ${cascadeCount.toLocaleString()} other deliveries${code}\nto match "${form.customer_name}"`,
            type: 'success',
            duration: 5000,
          });
        }, 4200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.error || err.message || 'Failed to save delivery',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  }

  function openEdit(row) {
    setForm({
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : '',
      plate_number: row.plate_number || '',
      so_number: row.so_number || '',
      document_no: row.document_no || '',
      invoice_number: row.invoice_number || '',
      customer_code: row.customer_code || '',
      customer_name: row.customer_name || '',
      amount: row.amount || '',
      picker: row.picker || '',
      checker_name: row.checker_name || '',
      driver: row.driver || '',
      helper_1: row.helper_1 || '',
      helper_2: row.helper_2 || '',
      destination: row.destination || '',
      date_delivered: row.date_delivered ? new Date(row.date_delivered).toISOString().slice(0, 10) : '',
      due_date: row.due_date ? new Date(row.due_date).toISOString().slice(0, 10) : '',
      payment_terms: row.payment_terms || row.payment_terms_code || '',
      delivery_status: row.delivery_status || 'PENDING',
      remarks: row.remarks || '',
      color: row.color || ''
    });
    setOriginalInvoiceNumber(row.invoice_number || '');
    setEditId(row.id);
    setShowModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Move this delivery to Deleted Deliveries archive? You can restore it later.')) return;
    try {
      await deliveryAPI.delete(id);
      setToast({ message: 'Delivery moved to archive', type: 'info', duration: 3000 });
      load(pagination.page);
    } catch (err) {
      setToast({ message: 'Error deleting: ' + (err.response?.data?.error || err.message), type: 'error', duration: 5000 });
    }
  }

  async function handleAutoLink() {
    if (!window.confirm('Auto-link unmatched deliveries to customers? This will update customer names to match exactly.')) return;
    try {
      const res = await fetch('/api/deliveries/auto-link-customers', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setToast({
        message: data.message + (data.unmatchedNames?.length ? `\n${data.unmatched} still unmatched` : ''),
        type: 'success',
        duration: 5000,
      });
      load(pagination.page);
    } catch (err) {
      setToast({ message: 'Auto-link failed: ' + err.message, type: 'error', duration: 5000 });
    }
  }

  async function handleMatchByAmount() {
    if (!window.confirm('Match deliveries to BC invoices by Amount Including VAT?\n\nThis will find deliveries without customer codes and match them to BC posted sales invoices using the amount.')) return;
    try {
      const res = await fetch('/api/bc/auto-match-customer-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company: 'YSU Marketing Corporation LIVE' })
      });
      const data = await res.json();
      setToast({
        message: `${data.message}${data.unmatched ? `\n${data.unmatched} unmatched` : ''}`,
        type: 'success',
        duration: 5000,
      });
      load(pagination.page);
    } catch (err) {
      setToast({ message: 'Match failed: ' + err.message, type: 'error', duration: 5000 });
    }
  }

  function clearFilters() {
    setSearch('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setDeliveredDateFrom('');
    setDeliveredDateTo('');
  }

  function handleSort(column) {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  }

  async function searchInvoice(number) {
    if (invoiceSearchTimer) clearTimeout(invoiceSearchTimer);

    if (!number || number.length < 3) {
      setInvoiceSuggestions([]);
      setShowInvoiceDropdown(false);
      setInvoiceLoading(false);
      return;
    }

    setInvoiceLoading(true);
    console.log('[Invoice Search] Searching for:', number);

    const timer = setTimeout(async () => {
      try {
        const url = `/api/bc/lookup-invoice?invoiceNumber=${encodeURIComponent(number)}&company=YSU+Marketing+Corporation+LIVE`;
        console.log('[Invoice Search] Fetching:', url);
        const res = await fetch(url, { credentials: 'include' });
        console.log('[Invoice Search] Response status:', res.status);
        const data = await res.json();
        console.log('[Invoice Search] Results:', data.data?.length || 0, data.data);
        if (data.data && data.data.length > 0) {
          setInvoiceSuggestions(data.data);
          setShowInvoiceDropdown(true);
          console.log('[Invoice Search] Dropdown shown');
        } else {
          setInvoiceSuggestions([]);
          setShowInvoiceDropdown(false);
          console.log('[Invoice Search] No results');
        }
      } catch (err) {
        console.error('[Invoice Search] Error:', err);
        setInvoiceSuggestions([]);
        setShowInvoiceDropdown(false);
      } finally {
        setInvoiceLoading(false);
      }
    }, 500);

    setInvoiceSearchTimer(timer);
  }

  function selectInvoice(invoice) {
    setForm(prev => ({
      ...prev,
      invoice_number: invoice.external_document_no || invoice.invoice_no,
      so_number: invoice.order_no || '',
      document_no: invoice.invoice_no || '',
      customer_code: invoice.sell_to_customer_no || '',
      customer_name: invoice.sell_to_customer_name || '',
      amount: invoice.amount_including_vat || invoice.amount || '',
      date: invoice.posting_date ? new Date(invoice.posting_date).toISOString().slice(0, 10) : prev.date,
      due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().slice(0, 10) : prev.due_date,
    }));
    setInvoiceSuggestions([]);
    setShowInvoiceDropdown(false);

    // Fetch payment terms from customers table
    if (invoice.sell_to_customer_no) {
      fetch(`/api/customers?search=${encodeURIComponent(invoice.sell_to_customer_no)}&limit=1`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.data && data.data.length > 0) {
            const customer = data.data[0];
            if (customer.payment_terms_code) {
              setForm(prev => ({ ...prev, payment_terms: customer.payment_terms_code }));
            }
          }
        })
        .catch(err => console.error('Failed to fetch customer payment terms:', err));
    }
  }

  function SortIcon({ column }) {
    if (sortBy !== column) return <ArrowUpDown size={12} style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortOrder === 'ASC'
      ? <ArrowUp size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />
      : <ArrowDown size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />;
  }

  function SortableTh({ column, children }) {
    const isActive = sortBy === column;
    return (
      <th
        onClick={() => handleSort(column)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          background: isActive ? '#f0f9ff' : undefined,
          transition: 'background 0.15s'
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ''; }}
        title={`Sort by ${children} ${isActive ? (sortOrder === 'ASC' ? '(ascending)' : '(descending)') : ''}`}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {children}
          <SortIcon column={column} />
        </span>
      </th>
    );
  }

  const hasActiveFilters = search || statusFilter || dateFrom || dateTo || deliveredDateFrom || deliveredDateTo;

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={() => setToast(null)} />}
      <div className="table-container">
        <div className="table-header deliveries-header table-title-row">
          <h3>Delivery Records ({pagination.total.toLocaleString()} total)</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={handleMatchByAmount} title="Match deliveries to BC invoices by Amount Including VAT" style={{ fontSize: 12, padding: '6px 12px', background: '#0891b2', color: 'white', border: 'none' }}>
              <Link2 size={14} /> Match by BC Amount
            </button>
            <button className="btn btn-secondary" onClick={handleAutoLink} title="Auto-link unmatched deliveries to customers" style={{ fontSize: 12, padding: '6px 12px' }}>
              <Link2 size={14} /> Link Customers
            </button>
            <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setEditId(null); setShowModal(true); }}>
              <Plus size={16} /> Add New
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="deliveries-filters">
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              className="search-input"
              style={{ width: '100%' }}
              placeholder="Search invoice, customer, driver..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Status */}
          <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Status</option>
            <option value="DELIVERED">Delivered</option>
            <option value="PENDING">Pending</option>
            <option value="RETURNED">Returned</option>
            <option value="IN TRANSIT">In Transit</option>
          </select>

          {/* Divider */}
          <div className="deliveries-filter-divider" style={{ width: 1, height: 28, background: '#e2e8f0', margin: '0 4px' }} />

          {/* Dispatch Date Range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CalendarDays size={14} style={{ color: '#64748b', flexShrink: 0 }} />
            <input type="date" className="filter-select" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Dispatch date from" />
            <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
            <input type="date" className="filter-select" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Dispatch date to" />
          </div>

          {/* Delivered Date Range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>Delivered</span>
            <input type="date" className="filter-select" value={deliveredDateFrom} onChange={e => setDeliveredDateFrom(e.target.value)} title="Delivered from" />
            <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
            <input type="date" className="filter-select" value={deliveredDateTo} onChange={e => setDeliveredDateTo(e.target.value)} title="Delivered to" />
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              className="deliveries-filter-clear"
              onClick={clearFilters}
              title="Clear all filters"
            >
              <RotateCcw size={12} /> Clear
            </button>
          )}
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortableTh column="date">Date</SortableTh>
                <SortableTh column="dispatch_id">Invoice #</SortableTh>
                <th>Customer Code</th>
                <SortableTh column="customer_name">Customer</SortableTh>
                <SortableTh column="amount">Amount</SortableTh>
                <SortableTh column="driver">Driver</SortableTh>
                <th>Helper 1</th>
                <th>Helper 2</th>
                <th>Picker 1</th>
                <th>Checker</th>
                <th>Plate #</th>
                <th>Destination</th>
                <th>Payment Terms</th>
                <th>Due Date</th>
                <SortableTh column="delivery_status">Status</SortableTh>
                <th>Date Delivered</th>
                <th>Date Recv. by AR</th>
                <th>Color</th>
                <th>PSI Doc Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={20} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
              ) : deliveries.length === 0 ? (
                <tr><td colSpan={20} style={{ textAlign: 'center', padding: 40 }}>No records found</td></tr>
              ) : deliveries.map((d, i) => (
                <tr key={`${d.id}-${i}`}>
                  <td>{formatDate(d.date)}</td>
                  <td>{d.invoice_number || '-'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.customer_code || '-'}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.customer_name || '-'}</td>
                  <td className="amount">{formatCurrency(d.amount)}</td>
                  <td>{d.driver || '-'}</td>
                  <td>{d.helper_1 || '-'}</td>
                  <td>{d.helper_2 || '-'}</td>
                  <td>{d.picker || '-'}</td>
                  <td>{d.checker_name || '-'}</td>
                  <td>{d.plate_number || '-'}</td>
                  <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.destination || '-'}</td>
                  <td>
                    {d.payment_terms_code ? (
                      <span className="status-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>{d.payment_terms_code}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      // Only show due date if delivery status is DELIVERED
                      const statusUpper = (d.delivery_status || '').toUpperCase();
                      const isDelivered = statusUpper.includes('DELIVER');
                      
                      if (!isDelivered) {
                        return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
                      }
                      
                      const dueDate = calculateDueDate(d.date_delivered, d.payment_terms_code || d.payment_terms);
                      if (!dueDate) {
                        return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
                      }
                      const overdue = isOverdue(dueDate);
                      return (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: 6, 
                          padding: '3px 10px', 
                          borderRadius: 12, 
                          fontSize: 12, 
                          fontWeight: 600,
                          background: overdue ? '#fee2e2' : '#d1fae5',
                          color: overdue ? '#991b1b' : '#065f46'
                        }}>
                          <span style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            background: overdue ? '#ef4444' : '#10b981' 
                          }}></span>
                          {formatDate(dueDate)}
                        </span>
                      );
                    })()}
                  </td>
                  <td><span className={`status-badge ${getStatusClass(d.delivery_status)}`}>{d.delivery_status || 'PENDING'}</span></td>
                  <td>{formatDate(d.date_delivered)}</td>
                  <td>
                    {d.color_ar ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: d.color_ar.toLowerCase() === 'green' ? '#d1fae5' : d.color_ar.toLowerCase() === 'yellow' ? '#fef3c7' : d.color_ar.toLowerCase() === 'red' ? '#fee2e2' : '#f1f5f9', color: d.color_ar.toLowerCase() === 'green' ? '#065f46' : d.color_ar.toLowerCase() === 'yellow' ? '#92400e' : d.color_ar.toLowerCase() === 'red' ? '#991b1b' : '#475569' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color_ar.toLowerCase() === 'green' ? '#10b981' : d.color_ar.toLowerCase() === 'yellow' ? '#f59e0b' : d.color_ar.toLowerCase() === 'red' ? '#ef4444' : '#94a3b8' }}></span>
                        {d.color_ar}
                      </span>
                    ) : (formatDate(d.date_received_by_ar) !== '-' ? formatDate(d.date_received_by_ar) : '-')}
                  </td>
                  <td>
                    {d.color ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: d.color.toLowerCase() === 'green' ? '#d1fae5' : d.color.toLowerCase() === 'yellow' ? '#fef3c7' : d.color.toLowerCase() === 'red' ? '#fee2e2' : '#f1f5f9', color: d.color.toLowerCase() === 'green' ? '#065f46' : d.color.toLowerCase() === 'yellow' ? '#92400e' : d.color.toLowerCase() === 'red' ? '#991b1b' : '#475569' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color.toLowerCase() === 'green' ? '#10b981' : d.color.toLowerCase() === 'yellow' ? '#f59e0b' : d.color.toLowerCase() === 'red' ? '#ef4444' : '#94a3b8' }}></span>
                        {d.color}
                      </span>
                    ) : '-'}
                  </td>
                  <td>{d.psi_posting_date ? formatDate(d.psi_posting_date) : (
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>No match</span>
                  )}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(d)} title="Edit"><Edit2 size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(d.id)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pagination">
        <div className="pagination-info">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} records)
        </div>
        <div className="pagination-buttons">
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(1)}>First</button>
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}><ChevronLeft size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)}><ChevronRight size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.totalPages)}>Last</button>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editId ? 'Edit Delivery' : 'New Delivery'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" name="date" value={form.date} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Plate Number</label>
                    <input name="plate_number" value={form.plate_number} onChange={handleChange} placeholder="e.g. KAU 3215" />
                  </div>
                  <div className="form-group">
                    <label>Order No</label>
                    <input name="so_number" value={form.so_number} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Document No</label>
                    <input name="document_no" value={form.document_no} onChange={handleChange} placeholder="Auto-filled from ERP" style={{ background: '#f8fafc' }} readOnly />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Invoice Number
                      {invoiceLoading && (
                        <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 400 }}>
                          Searching ERP...
                        </span>
                      )}
                    </label>
                    <input
                      ref={invoiceInputRef}
                      name="invoice_number"
                      value={form.invoice_number}
                      onChange={e => {
                        handleChange(e);
                        searchInvoice(e.target.value);
                      }}
                      onFocus={() => invoiceSuggestions.length > 0 && setShowInvoiceDropdown(true)}
                      onBlur={() => setTimeout(() => setShowInvoiceDropdown(false), 200)}
                      placeholder="Type to search ERP..."
                      autoComplete="off"
                    />
                  </div>
                  {showInvoiceDropdown && invoiceSuggestions.length > 0 && (
                    <div
                      style={{
                        position: 'fixed',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 9999,
                        maxHeight: 200,
                        overflowY: 'auto'
                      }}
                      ref={el => {
                        if (el && invoiceInputRef.current) {
                          const rect = invoiceInputRef.current.getBoundingClientRect();
                          el.style.top = `${rect.bottom + 4}px`;
                          el.style.left = `${rect.left}px`;
                          el.style.width = `${rect.width}px`;
                        }
                      }}
                    >
                      {invoiceSuggestions.map((inv, idx) => (
                        <div
                          key={idx}
                          onMouseDown={() => selectInvoice(inv)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: idx < invoiceSuggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                            fontSize: 13
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>
                            {inv.external_document_no || inv.invoice_no}
                          </div>
                          <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                            {inv.sell_to_customer_name} • {formatCurrency(inv.amount_including_vat || inv.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="form-group">
                    <label>Customer Code</label>
                    <input name="customer_code" value={form.customer_code} onChange={handleChange} placeholder="e.g. 13-MKI-100001" style={{ fontFamily: 'monospace' }} />
                  </div>
                  <div className="form-group full-width">
                    <label>Customer Name</label>
                    <input name="customer_name" value={form.customer_name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Amount (PHP)</label>
                    <input type="number" step="0.01" name="amount" value={form.amount} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Driver</label>
                    <input name="driver" value={form.driver} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Picker</label>
                    <input name="picker" value={form.picker} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Checker</label>
                    <input name="checker_name" value={form.checker_name} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Helper 1</label>
                    <input name="helper_1" value={form.helper_1} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Helper 2</label>
                    <input name="helper_2" value={form.helper_2} onChange={handleChange} />
                  </div>
                  <div className="form-group full-width">
                    <label>Destination</label>
                    <input name="destination" value={form.destination} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Date Delivered</label>
                    <input type="date" name="date_delivered" value={form.date_delivered} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" name="due_date" value={form.due_date} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms</label>
                    <input name="payment_terms" value={form.payment_terms} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="delivery_status" value={form.delivery_status} onChange={handleChange}>
                      <option value="PENDING">Pending</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="IN TRANSIT">In Transit</option>
                      <option value="RETURNED">Returned</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Remarks</label>
                    <textarea name="remarks" value={form.remarks} onChange={handleChange} rows={2} />
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input name="color" value={form.color} onChange={handleChange} placeholder="e.g., Green, Yellow, Red" style={{ padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90, justifyContent: 'center' }}>
                  {saving && (
                    <span style={{
                      width: 14, height: 14,
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      display: 'inline-block',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  )}
                  {saving ? 'Saving...' : (editId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
